import * as pc from 'playcanvas';
import type { Vec3, WorldAxis } from '@viewer/overlay/measureTypes';

const AXES: WorldAxis[] = ['x', 'y', 'z'];
const SHAFT_RADIUS = 0.005;
const ARROW_RADIUS = 0.014;
const ARROW_LENGTH = 0.045;

const AXIS_RGB: Record<WorldAxis, [number, number, number]> = {
  x: [0.92, 0.28, 0.24],
  y: [0.22, 0.85, 0.45],
  z: [0.28, 0.62, 0.95],
};

type AxisTier = 'idle' | 'hover' | 'active';

interface AxisEntities {
  shaft: pc.Entity;
  arrow: pc.Entity;
  shaftMat: pc.StandardMaterial;
  arrowMat: pc.StandardMaterial;
}

export interface GizmoHighlight {
  endpoint: 'a' | 'b';
  axis: WorldAxis;
}

export interface GizmoSyncState {
  visible: boolean;
  pointA: Vec3 | null;
  pointB: Vec3 | null;
  lengthA: number;
  lengthB: number;
  hover: GizmoHighlight | null;
  active: GizmoHighlight | null;
}

function styleAxisMaterial(
  mat: pc.StandardMaterial,
  axis: WorldAxis,
  tier: AxisTier,
): void {
  const [r, g, b] = AXIS_RGB[axis];
  mat.emissive.set(r, g, b);
  mat.emissiveIntensity = tier === 'active' ? 2.4 : tier === 'hover' ? 1.55 : 0.65;
  mat.opacity = tier === 'idle' ? 0.42 : tier === 'hover' ? 0.82 : 1;
  const opaque = tier === 'active';
  mat.blendType = opaque ? pc.BLEND_NONE : pc.BLEND_NORMAL;
  mat.depthWrite = opaque;
  mat.depthTest = true;
  mat.update();
}

const AXIS_Z = new pc.Vec3(0, 0, 1);

function axisUnit(axis: WorldAxis): pc.Vec3 {
  if (axis === 'x') return pc.Vec3.RIGHT;
  if (axis === 'y') return pc.Vec3.UP;
  return AXIS_Z;
}

/**
 * World-space translate gizmo — axis shafts + cone arrowheads, depth-tested like panels.
 * All parts are placed in world coordinates (same pattern as measure line boxes).
 */
export class PlayCanvasMeasureGizmo {
  private readonly root: pc.Entity;
  private readonly axesA: Record<WorldAxis, AxisEntities>;
  private readonly axesB: Record<WorldAxis, AxisEntities>;
  private readonly tmpMid = new pc.Vec3();
  private readonly tmpTip = new pc.Vec3();
  private readonly aimQuat = new pc.Quat();

  constructor(parent: pc.Entity) {
    this.root = new pc.Entity('measure-gizmo');
    this.root.enabled = false;
    parent.addChild(this.root);
    this.axesA = this.createAxes('measure-gizmo-a');
    this.axesB = this.createAxes('measure-gizmo-b');
  }

  dispose(): void {
    this.disposeAxes(this.axesA);
    this.disposeAxes(this.axesB);
    this.root.destroy();
  }

  sync(state: GizmoSyncState): void {
    this.root.enabled = state.visible;
    if (!state.visible) return;

    this.syncPoint(this.axesA, state.pointA, state.lengthA, 'a', state.hover, state.active);
    this.syncPoint(this.axesB, state.pointB, state.lengthB, 'b', state.hover, state.active);
  }

  static axisLengthAtPoint(point: Vec3, camPos: pc.Vec3): number {
    const dx = point.x - camPos.x;
    const dy = point.y - camPos.y;
    const dz = point.z - camPos.z;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    return Math.max(0.14, Math.min(0.42, dist * 0.1));
  }

  private createAxes(prefix: string): Record<WorldAxis, AxisEntities> {
    const axes = {} as Record<WorldAxis, AxisEntities>;
    for (const axis of AXES) {
      const shaftMat = this.makeAxisMaterial(axis, 'idle');
      const arrowMat = this.makeAxisMaterial(axis, 'idle');
      const shaft = this.createShaft(`${prefix}-${axis}-shaft`, shaftMat);
      const arrow = this.createArrow(`${prefix}-${axis}-arrow`, arrowMat);
      this.root.addChild(shaft);
      this.root.addChild(arrow);
      axes[axis] = { shaft, arrow, shaftMat, arrowMat };
    }
    return axes;
  }

  private disposeAxes(axes: Record<WorldAxis, AxisEntities>): void {
    for (const axis of AXES) {
      const a = axes[axis];
      a.shaft.destroy();
      a.arrow.destroy();
      a.shaftMat.destroy();
      a.arrowMat.destroy();
    }
  }

  private makeAxisMaterial(axis: WorldAxis, tier: AxisTier): pc.StandardMaterial {
    const mat = new pc.StandardMaterial();
    mat.diffuse.set(0, 0, 0);
    mat.useLighting = false;
    styleAxisMaterial(mat, axis, tier);
    return mat;
  }

  private createShaft(name: string, material: pc.Material): pc.Entity {
    const entity = new pc.Entity(name);
    entity.addComponent('render', {
      type: 'box',
      material,
      castShadows: false,
      receiveShadows: false,
      layers: [pc.LAYERID_WORLD],
    });
    this.tagDepth(entity);
    return entity;
  }

  private createArrow(name: string, material: pc.Material): pc.Entity {
    const entity = new pc.Entity(name);
    entity.addComponent('render', {
      type: 'cone',
      material,
      castShadows: false,
      receiveShadows: false,
      layers: [pc.LAYERID_WORLD],
    });
    this.tagDepth(entity);
    return entity;
  }

  private tagDepth(entity: pc.Entity): void {
    if (!entity.render) return;
    for (const mi of entity.render.meshInstances) {
      mi.drawOrder = 0;
    }
  }

  private syncPoint(
    axes: Record<WorldAxis, AxisEntities>,
    origin: Vec3 | null,
    axisLength: number,
    endpointId: 'a' | 'b',
    hover: GizmoHighlight | null,
    active: GizmoHighlight | null,
  ): void {
    if (!origin || axisLength <= 0.05) {
      for (const axis of AXES) {
        axes[axis].shaft.enabled = false;
        axes[axis].arrow.enabled = false;
      }
      return;
    }

    for (const axis of AXES) {
      const tier: AxisTier =
        active?.endpoint === endpointId && active.axis === axis
          ? 'active'
          : hover?.endpoint === endpointId && hover.axis === axis
            ? 'hover'
            : 'idle';
      const ents = axes[axis];
      styleAxisMaterial(ents.shaftMat, axis, tier);
      styleAxisMaterial(ents.arrowMat, axis, tier);

      const unit = axisUnit(axis);
      const shaftLen = Math.max(0.04, axisLength - ARROW_LENGTH);
      this.placeShaft(ents.shaft, origin, unit, shaftLen, tier);
      this.placeArrow(ents.arrow, origin, unit, shaftLen, tier);
    }
  }

  /** Box shaft aligned along +unit from origin — matches measure line placement. */
  private placeShaft(
    entity: pc.Entity,
    origin: Vec3,
    unit: pc.Vec3,
    length: number,
    tier: AxisTier,
  ): void {
    entity.enabled = true;
    this.tmpMid.set(
      origin.x + unit.x * length * 0.5,
      origin.y + unit.y * length * 0.5,
      origin.z + unit.z * length * 0.5,
    );
    this.tmpTip.set(
      origin.x + unit.x * length,
      origin.y + unit.y * length,
      origin.z + unit.z * length,
    );
    entity.setLocalPosition(this.tmpMid.x, this.tmpMid.y, this.tmpMid.z);
    entity.up.set(0, 1, 0);
    entity.lookAt(this.tmpTip.x, this.tmpTip.y, this.tmpTip.z);
    const radius = tier === 'active' ? SHAFT_RADIUS * 1.35 : SHAFT_RADIUS;
    entity.setLocalScale(radius * 2, radius * 2, length);
  }

  /** Cone arrowhead at the positive end of the axis (tip points along +unit). */
  private placeArrow(
    entity: pc.Entity,
    origin: Vec3,
    unit: pc.Vec3,
    shaftLen: number,
    tier: AxisTier,
  ): void {
    entity.enabled = true;
    const centerAlong = shaftLen + ARROW_LENGTH * 0.5;
    entity.setLocalPosition(
      origin.x + unit.x * centerAlong,
      origin.y + unit.y * centerAlong,
      origin.z + unit.z * centerAlong,
    );
    this.aimQuat.setFromDirections(pc.Vec3.UP, unit);
    entity.setLocalRotation(this.aimQuat);
    const radius = tier === 'active' ? ARROW_RADIUS * 1.25 : ARROW_RADIUS;
    entity.setLocalScale(radius * 2, ARROW_LENGTH, radius * 2);
  }
}
